import { ACTION_ICONS_NAMESPACE, CustomButton, TableActionType } from '@ui-kit'
import { FieldValues, Path, PathValue, UseFormSetValue } from 'react-hook-form'
import { Dispatch, SetStateAction } from 'react'
import { ElementType, EntityLayout, LayoutData } from '../types/layout.types'
import {
  APPLICATION_PREFERENCES_STRINGS,
  ADVANCE_SEARCH_CONFIG_STRINGS,
  DUPLICATION,
} from '../utils/strings'
import FormRenderer from './FormRenderer'
import { MODE_CONSTANTS, NAMESPACES } from '../utils/constants'
import { noop, isEmpty } from 'lodash'
import SearchLayout from '../pages/call-interaction/SearchLayout'
import { ORGANIZATION_ENTITY } from '../pages/intake-ques/IntakeQueReview'
import { AdvancedSearchInfo } from '../pages/call-interaction/RequestorSearchForm'
import { ListingTable } from './listing-table/ListingTable'
import { ListingTableRow } from '../types/listingTable.types'
import { DuplicationScore } from './DuplicationScore'
import { Group } from '../types/types'
import {
  DataTypeStore,
  getEntityDataByEntityFilter,
  LayoutResponseType,
} from '@app-store'
import { ListingDataResponse, ListingTableColumn } from '../utils/types'

interface AdvancedSearchTabProps<T extends FieldValues> {
  element: ElementType
  editEntityRecord: boolean | undefined
  addEntityRecord: boolean | undefined
  isViewClicked: boolean
  isDuplicateModalOpen: boolean
  viewDuplicateRecord: boolean
  editLayout: LayoutResponseType | undefined
  viewLayout: LayoutResponseType | undefined
  listingLayout: LayoutResponseType | undefined
  editRecordData: FieldValues | undefined
  addedRecord: FieldValues | null
  viewEntityRecord: DataTypeStore | undefined
  advancedFiltersData: AdvancedSearchInfo | undefined
  dupData: FieldValues | undefined
  selectedRow: ListingTableRow | null
  refetchKey: number
  buttonRef: React.RefObject<HTMLButtonElement>
  triggerSearch: boolean
  defaultAdvancedSearch?: boolean
  fieldKeyName?: string
  showHeader?: boolean
  cancelClick: () => void
  triggerClick: () => void
  editSelectedRecordAdvanceSearh: (values: FieldValues) => Promise<void>
  createNewRecord: (values?: FieldValues) => Promise<void>
  setAdvancedFiltersData: Dispatch<
    SetStateAction<AdvancedSearchInfo | undefined>
  >
  setViewEntityRecord: Dispatch<SetStateAction<DataTypeStore | undefined>>
  setEditEntityRecord: Dispatch<SetStateAction<boolean | undefined>>
  setAddEntityRecord: Dispatch<SetStateAction<boolean | undefined>>
  setIsViewClicked: Dispatch<SetStateAction<boolean>>
  setIsDuplicateModalOpen: Dispatch<SetStateAction<boolean>>
  setSelectedRow: Dispatch<SetStateAction<ListingTableRow | null>>
  setValue: UseFormSetValue<T>
  setIsAdvancedSearch: Dispatch<SetStateAction<boolean>>
  setRefetchKey: Dispatch<SetStateAction<number>>
  setViewDuplicateRecord: Dispatch<SetStateAction<boolean>>
  fetchTableDataCallback: (
    values: FieldValues,
    listingLayoutId: string | number,
    payload?: getEntityDataByEntityFilter['payload'] & { layout_id: number }
  ) => Promise<ListingDataResponse>
  fetchViewLayout: () => Promise<number | undefined>
  fetchEditRecordData: (
    layoutId: number,
    recordId?: string | number
  ) => Promise<void>
  onLookupRowSelect?: (row: DataTypeStore) => void
  selectedProgramId?: string
  selectedProductId?: string
  onRecordSelected?: (record: DataTypeStore) => void
}

export const AdvancedSearchTab = <T extends FieldValues>({
  element,
  editEntityRecord,
  addEntityRecord,
  isViewClicked,
  isDuplicateModalOpen,
  viewDuplicateRecord,
  editLayout,
  viewLayout,
  listingLayout,
  editRecordData,
  addedRecord,
  viewEntityRecord,
  advancedFiltersData,
  dupData,
  selectedRow,
  refetchKey,
  buttonRef,
  triggerSearch,
  defaultAdvancedSearch,
  showHeader = true,
  cancelClick,
  triggerClick,
  editSelectedRecordAdvanceSearh,
  createNewRecord,
  setAdvancedFiltersData,
  setViewEntityRecord,
  setEditEntityRecord,
  setAddEntityRecord,
  setIsViewClicked,
  setIsDuplicateModalOpen,
  setSelectedRow,
  setValue,
  setIsAdvancedSearch,
  setRefetchKey,
  setViewDuplicateRecord,
  fetchTableDataCallback,
  fetchViewLayout,
  fetchEditRecordData,
  onLookupRowSelect,
  selectedProgramId,
  selectedProductId,
  onRecordSelected,
}: AdvancedSearchTabProps<T>) => {
  const completeRecordSelection = (record: DataTypeStore) => {
    if (onRecordSelected) {
      onRecordSelected(record)
      cancelClick()
      setIsDuplicateModalOpen(false)
      setIsAdvancedSearch(false)
      return
    }

    setValue(
      element.keyName as Path<T>,
      { id: Number(record.record_id ?? record.id) } as PathValue<T, Path<T>>
    )
    cancelClick()
    setIsDuplicateModalOpen(false)
    setIsAdvancedSearch(false)
  }
  const extraColums: ListingTableColumn[] = [
    {
      header: '',
      accessor: DUPLICATION.SELECT_ACCESSOR,
      type: DUPLICATION.RADIO_TYPE,
      onClick(_KeyName, row, _rowIndex) {
        setRefetchKey((prev) => prev + 1)
        setSelectedRow(row)
      },
    },
    {
      header: APPLICATION_PREFERENCES_STRINGS.MATCH_SCORE,
      accessor: DUPLICATION.SCORE_ACCESSOR,
      type: DUPLICATION.COMPONENT_TYPE,
      renderComponent: (row) => {
        return (
          <div className="flex items-center gap-1">
            <span>{String(row?.score ?? '')}</span>
            <DuplicationScore
              groups={row?.matchDetails as unknown as Group[]}
            />
          </div>
        )
      },
    },
  ]

  const extraActions: (TableActionType<DataTypeStore> & {
    onClick?: (row: ListingTableRow, rowIndex: number) => void
  })[] = [
    {
      label: NAMESPACES.VIEW,
      key: ACTION_ICONS_NAMESPACE.VIEW,
      onClick: async (row) => {
        const layoutId = await fetchViewLayout()
        setSelectedRow(row)
        setViewDuplicateRecord(true)
        fetchEditRecordData(layoutId ?? 0, row?.id)
      },
    },
  ]

  if (editEntityRecord) {
    const breadcrumbs = [
      {
        label: APPLICATION_PREFERENCES_STRINGS?.ADVANCED_SEARCH,
        onClick: cancelClick,
      },
      {
        label: `/ ${NAMESPACES.EDIT} ${element?.meta?.label}`,
        isActive: true,
      },
    ]

    return (
      <div>
        {showHeader && (
          <div className="flex justify-between items-center mb-2">
            <div>
              {breadcrumbs.map((crumb, index) => (
                <span
                  key={index}
                  className={`font-medium text-base font-primary ${
                    crumb.onClick ? 'cursor-pointer' : ''
                  } ${crumb.isActive ? 'text-primary-500' : ''}`}
                  onClick={crumb.onClick}
                >
                  {crumb.label}
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <CustomButton onClick={cancelClick}>
                {APPLICATION_PREFERENCES_STRINGS?.CANCEL}
              </CustomButton>
              <CustomButton isFilled onClick={triggerClick}>
                {APPLICATION_PREFERENCES_STRINGS?.UPDATE}
              </CustomButton>
            </div>
          </div>
        )}
        <FormRenderer
          heading={
            <>
              <button
                ref={buttonRef}
                type="submit"
                className="cursor-pointer pr-[6px] hidden"
              />
            </>
          }
          layout={editLayout?.layout_data as LayoutData}
          mode={MODE_CONSTANTS?.EDIT}
          data={editRecordData}
          handleFormSubmit={editSelectedRecordAdvanceSearh}
          fullLayout={editLayout as EntityLayout}
        />
      </div>
    )
  }

  if (addEntityRecord) {
    if (!isDuplicateModalOpen) {
      const breadcrumbs = [
        {
          label: APPLICATION_PREFERENCES_STRINGS?.ADVANCED_SEARCH,
          onClick: cancelClick,
        },
        {
          label: `/ ${NAMESPACES.ADD} ${element?.meta?.label}`,
          isActive: true,
        },
      ]

      return (
        <div>
          {showHeader && (
            <div className="flex justify-between items-center mb-2">
              <div>
                {breadcrumbs.map((crumb, index) => (
                  <span
                    key={index}
                    className={`font-medium text-base font-primary ${
                      crumb.onClick ? 'cursor-pointer' : ''
                    } ${crumb.isActive ? 'text-primary-500' : ''}`}
                    onClick={crumb.onClick}
                  >
                    {crumb.label}
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <CustomButton onClick={cancelClick}>
                  {APPLICATION_PREFERENCES_STRINGS?.CANCEL}
                </CustomButton>
                <CustomButton isFilled onClick={triggerClick}>
                  {APPLICATION_PREFERENCES_STRINGS?.NEXT}
                </CustomButton>
              </div>
            </div>
          )}
          <FormRenderer
            heading={
              <>
                <button
                  ref={buttonRef}
                  type="submit"
                  className="cursor-pointer pr-[6px] hidden"
                />
              </>
            }
            layout={editLayout?.layout_data as LayoutData}
            mode={MODE_CONSTANTS?.ADD}
            handleFormSubmit={editSelectedRecordAdvanceSearh}
            fullLayout={editLayout as EntityLayout}
            data={addedRecord}
          />
        </div>
      )
    }

    if (!viewDuplicateRecord) {
      const breadcrumbs = [
        {
          label: APPLICATION_PREFERENCES_STRINGS?.ADVANCED_SEARCH,
          onClick: cancelClick,
        },
        {
          label: `/ ${NAMESPACES.ADD} ${element?.meta?.label} / `,
          onClick: () => {
            setIsDuplicateModalOpen(false)
            setSelectedRow(null)
          },
        },
        {
          label: APPLICATION_PREFERENCES_STRINGS?.DUPLICATE_HEADING,
          isActive: true,
        },
      ]

      return (
        <div>
          {showHeader && (
            <div className="flex justify-between items-center mb-2">
              <div>
                {breadcrumbs.map((crumb, index) => (
                  <span
                    key={index}
                    className={`font-medium text-base font-primary ${
                      crumb.onClick ? 'cursor-pointer' : ''
                    } ${crumb.isActive ? 'text-primary-500' : ''}`}
                    onClick={crumb.onClick}
                  >
                    {crumb.label}
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <CustomButton isFilled onClick={createNewRecord}>
                  {APPLICATION_PREFERENCES_STRINGS?.CREATE_ANYWAY}
                </CustomButton>
                <CustomButton
                  isFilled
                  onClick={() => {
                    completeRecordSelection(selectedRow as DataTypeStore)
                  }}
                  disabled={isEmpty(selectedRow)}
                >
                  {APPLICATION_PREFERENCES_STRINGS?.USE_EXISTING}
                </CustomButton>
              </div>
            </div>
          )}
          <span className="font-light text-xs pb-2">
            {dupData?.totalElements}{' '}
            {dupData?.totalElements > 1
              ? `${APPLICATION_PREFERENCES_STRINGS.RECORD}s`
              : APPLICATION_PREFERENCES_STRINGS.RECORD}
            {` ${APPLICATION_PREFERENCES_STRINGS.FOUND}`}
          </span>
          <ListingTable
            entityId={Number(
              element?.meta?.relationship?.target_entity === 'c_organizations'
                ? ORGANIZATION_ENTITY.value
                : Number(element?.meta?.relationship?.target_entity_id) ?? 0
            )}
            entityName={
              ADVANCE_SEARCH_CONFIG_STRINGS.ADVANCE_SEARCH_ENTITY_MAP[
                (element?.meta?.relationship?.target_entity ??
                  '') as keyof typeof ADVANCE_SEARCH_CONFIG_STRINGS.ADVANCE_SEARCH_ENTITY_MAP
              ] ?? ''
            }
            extraActions={extraActions}
            layout={listingLayout}
            dataFetchCallback={(payload) =>
              fetchTableDataCallback(
                addedRecord ?? {},
                listingLayout?.id ?? 0,
                payload
              )
            }
            extraColumns={extraColums}
            hideFilters={true}
            refetchKey={refetchKey}
            openInNewTab={true}
          />
        </div>
      )
    }

    const breadcrumbs = [
      {
        label: APPLICATION_PREFERENCES_STRINGS?.ADVANCED_SEARCH,
        onClick: cancelClick,
      },
      {
        label: `/ ${NAMESPACES.ADD} ${element?.meta?.label} / `,
        onClick: () => {
          setIsDuplicateModalOpen(false)
        },
      },
      {
        label: APPLICATION_PREFERENCES_STRINGS?.DUPLICATE_HEADING,
        onClick: () => {
          setViewDuplicateRecord(false)
        },
      },
      {
        label: ` / ${NAMESPACES.VIEW} ${element?.meta?.label}`,
        isActive: true,
      },
    ]

    return (
      <FormRenderer
        heading={
          showHeader ? (
            <div className="flex justify-between items-center mb-2">
              <div>
                {breadcrumbs.map((crumb, index) => (
                  <span
                    key={index}
                    className={`font-medium text-base font-primary ${
                      crumb.onClick ? 'cursor-pointer' : ''
                    } ${crumb.isActive ? 'text-primary-500' : ''}`}
                    onClick={crumb.onClick}
                  >
                    {crumb.label}
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <CustomButton isFilled onClick={createNewRecord}>
                  {APPLICATION_PREFERENCES_STRINGS?.CREATE_ANYWAY}
                </CustomButton>
                <CustomButton
                  isFilled
                  onClick={() => {
                    completeRecordSelection(selectedRow as DataTypeStore)
                  }}
                  disabled={isEmpty(selectedRow)}
                >
                  {APPLICATION_PREFERENCES_STRINGS?.USE_EXISTING}
                </CustomButton>
              </div>
            </div>
          ) : (
            <></>
          )
        }
        layout={viewLayout?.layout_data as LayoutData}
        mode={MODE_CONSTANTS?.VIEW}
        handleFormSubmit={noop}
        fullLayout={viewLayout as EntityLayout}
        data={editRecordData}
      />
    )
  }

  if (isViewClicked) {
    const breadcrumbs = [
      {
        label: APPLICATION_PREFERENCES_STRINGS?.ADVANCED_SEARCH,
        onClick: cancelClick,
      },
      {
        label: `/ ${NAMESPACES.VIEW} ${element?.meta?.label}`,
        isActive: true,
      },
    ]

    return (
      <div>
        {showHeader && (
          <div className="flex justify-between items-center mb-2">
            <div>
              {breadcrumbs.map((crumb, index) => (
                <span
                  key={index}
                  className={`font-medium text-base font-primary ${
                    crumb.onClick ? 'cursor-pointer' : ''
                  } ${crumb.isActive ? 'text-primary-500' : ''}`}
                  onClick={crumb.onClick}
                >
                  {crumb.label}
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <CustomButton onClick={cancelClick}>
                {APPLICATION_PREFERENCES_STRINGS?.CANCEL}
              </CustomButton>
              <CustomButton
                isFilled
                onClick={() => {
                  if (viewEntityRecord) {
                    completeRecordSelection(viewEntityRecord)
                  }
                }}
              >
                {APPLICATION_PREFERENCES_STRINGS?.SELECT}
              </CustomButton>
            </div>
          </div>
        )}
        <FormRenderer
          heading={
            <>
              <button
                ref={buttonRef}
                type="submit"
                className="cursor-pointer pr-[6px] hidden"
              />
            </>
          }
          layout={viewLayout?.layout_data as LayoutData}
          mode={MODE_CONSTANTS?.VIEW}
          data={editRecordData}
          handleFormSubmit={editSelectedRecordAdvanceSearh}
          fullLayout={viewLayout as EntityLayout}
        />
      </div>
    )
  }

  return (
    <div
      className={`${
        editEntityRecord || addEntityRecord || isViewClicked ? 'hidden' : ''
      }`}
    >
      {showHeader && (
        <div className="flex justify-between items-center mb-2">
          <span className="font-medium text-base font-primary">
            {APPLICATION_PREFERENCES_STRINGS?.ADVANCED_SEARCH}
          </span>
          <CustomButton
            onClick={() => {
              setIsAdvancedSearch(false)
              setEditEntityRecord(false)
              setAddEntityRecord(false)
            }}
          >
            {APPLICATION_PREFERENCES_STRINGS?.CANCEL}
          </CustomButton>
        </div>
      )}
      <SearchLayout
        isLookupSearch={true}
        defaultAdvancedSearch={defaultAdvancedSearch}
        entity_id={
          element?.meta?.relationship?.target_entity === 'c_organizations'
            ? ORGANIZATION_ENTITY.value
            : element?.meta?.relationship?.target_entity_id?.toString() ?? ''
        }
        entity_name={
          ADVANCE_SEARCH_CONFIG_STRINGS.ADVANCE_SEARCH_ENTITY_MAP[
            (element?.meta?.relationship?.target_entity ??
              '') as keyof typeof ADVANCE_SEARCH_CONFIG_STRINGS.ADVANCE_SEARCH_ENTITY_MAP
          ] ?? ''
        }
        organizationType={undefined}
        selectedProgramId={selectedProgramId}
        selectedProductId={selectedProductId}
        setViewEntityRecord={setViewEntityRecord}
        viewEntityRecord={viewEntityRecord}
        advancedFiltersData={advancedFiltersData}
        setAdvancedFiltersData={setAdvancedFiltersData}
        setEditEntityRecord={setEditEntityRecord}
        setAddEntityRecord={setAddEntityRecord}
        setIsViewClicked={setIsViewClicked}
        isLookup={true}
        triggerSearch={triggerSearch}
        onLookupRowSelect={onLookupRowSelect}
      />
    </div>
  )
}
